// Clean Room Configuration System
// Simple texture management

// Available texture sets
const TEXTURES = {
    woodFloor: {
        path: `${process.env.PUBLIC_URL}/textures/tiles/`,
        color: 'WoodFloor014_1K-JPG_Color.jpg',
        normal: 'WoodFloor014_1K-JPG_NormalGL.jpg',
        roughness: 'WoodFloor014_1K-JPG_Roughness.jpg',
        repeat: [8, 8]
    },
    marbleFloor: {
        path: `${process.env.PUBLIC_URL}/textures/marble/`,
        color: 'Marble005_1K-JPG_Color.jpg',
        normal: 'Marble005_1K-JPG_NormalGL.jpg',
        roughness: 'Marble005_1K-JPG_Roughness.jpg',
        repeat: [6, 6]
    },
    plasterWalls: {
        path: `${process.env.PUBLIC_URL}/textures/plaster/`,
        color: 'Plaster001_1K-JPG_Color.jpg',
        normal: 'Plaster001_1K-JPG_NormalGL.jpg',
        roughness: 'Plaster001_1K-JPG_Roughness.jpg',
        repeat: [2, 2]
    }
};

// Base room configuration that all rooms inherit from
export const BASE_ROOM_CONFIG = {
    dimensions: {
        width: 35,
        depth: 25,
        height: 8,
        doorWidth: 4,
        wallThickness: 0.2
    },
    
    displayConfig: {
        spacing: 3,
        heightFromFloor: 3,
        margin: 4,
    },

    // Player spawn point - where the character starts in the room
    spawnPoint: {
        position: [0, 0, 8], // x, y, z coordinates - moved closer to center
        rotation: [0, 0, 0],  // x, y, z rotation in radians
        cameraPosition: [0, 6, 8], // camera at character position, reasonable height
        cameraLookAt: [0, 3, 0] // look toward center at eye level
    },

    // Clean material system - solid colors only
    materials: {
        floor: {
            color: "#f5f5f5",
            roughness: 0.3,
            metalness: 0.1
        },
        walls: {
            color: "#ffffff",
            roughness: 0.8,
            metalness: 0
        },
        ceiling: {
            color: "#ffffff",
            roughness: 0.7,
            metalness: 0
        }
    },
    
    podiumPositions: [
        // BETA: Podiums removed for cleaner gallery look
        // [0, 0, 0],
        // [0, 0, 8], 
        // [0, 0, -8]
    ]
};

// Clean room configurations
export const ROOM_CONFIGS = {
    classic: {
        ...BASE_ROOM_CONFIG,
        materials: {
            floor: {
                color: "#f8f4f0",
                roughness: 0.99, // Almost completely matte
                metalness: 0.0 // No metalness at all
            },
            walls: {
                color: "#ffffff",
                roughness: 0.8,
                metalness: 0
            },
            ceiling: {
                color: "#ffffff",
                roughness: 0.7,
                metalness: 0
            }
        },
        textures: {
            floor: TEXTURES.woodFloor,
            walls: TEXTURES.plasterWalls,
            ceiling: null
        }
    },
    
    spacious: {
        ...BASE_ROOM_CONFIG,
        dimensions: {
            width: 45,
            depth: 35,
            height: 10,
            doorWidth: 6,
            wallThickness: 0.2
        },
        displayConfig: {
            spacing: 4,
            heightFromFloor: 4,
            margin: 5,
        },
        materials: {
            floor: {
                color: "#2a2a2a",
                roughness: 0.99, // Almost completely matte
                metalness: 0.0 // No metalness at all
            },
            walls: {
                color: "#f5f5f5",
                roughness: 0.8,
                metalness: 0
            },
            ceiling: {
                color: "#2a2a2a",
                roughness: 0.6,
                metalness: 0.1
            }
        },
        textures: {
            floor: null,
            walls: null,
            ceiling: null
        },
        podiumPositions: [
            [0, 0, 0],
            [-10, 0, 10],
            [10, 0, 10],
            [-10, 0, -10],
            [10, 0, -10]
        ]
    },
    
    hexagonal: {
        ...BASE_ROOM_CONFIG,
        dimensions: {
            width: 30,
            depth: 30,
            height: 8,
            doorWidth: 0,
            wallThickness: 0.2
        },
        displayConfig: {
            spacing: 4,
            heightFromFloor: 4,
            margin: 5,
        },
        // Spawn in the entry room facing the hallway towards the hexagonal gallery
        spawnPoint: {
            position: [25, 0, 14], // Start more centered in the entry room
            rotation: [0, 1.047, 0], // Face towards the hallway
            cameraPosition: [25, 6, 14], // Camera at character position, elevated
            cameraLookAt: [21, 3, 12] // Look towards the hallway entrance
        },
        materials: {
            floor: {
                color: "#e8e8e8",
                roughness: 0.99, // Almost completely matte
                metalness: 0.0 // No metalness at all
            },
            walls: {
                color: "#ffffff",
                roughness: 0.8,
                metalness: 0
            },
            ceiling: {
                color: "#ffffff",
                roughness: 0.7,
                metalness: 0
            }
        },
        textures: {
            floor: TEXTURES.marbleFloor,
            walls: TEXTURES.plasterWalls,
            ceiling: null
        },
        podiumPositions: []
    }
};

// Simple room getter function
export function getRoomConfig(roomType, glbPath = null) {
    if (!roomType || typeof roomType !== 'string') {
        return ROOM_CONFIGS.classic;
    }
    
    // Handle custom GLB room type
    if (roomType === 'custom-glb') {
        return {
            ...BASE_ROOM_CONFIG,
            isGlbRoom: true,
            glbPath: glbPath || `${process.env.PUBLIC_URL}/rooms/RoomTemplate.glb`
        };
    }
    
    return ROOM_CONFIGS[roomType] || ROOM_CONFIGS.classic;
}

// Available room types
export const ROOM_TYPES = {
    CLASSIC: 'classic',
    SPACIOUS: 'spacious', 
    HEXAGONAL: 'hexagonal',
    CUSTOM_GLB: 'custom-glb'
};